
import { Component, Vue, Prop } from 'vue-property-decorator'
import { actionTypesModule, commonModule, itemsModule, offersModule } from '@/store/modules/store-accessor'
import { formatAmount, getOptimalTextColor } from '@/helpers/data'
import { greekSearchFilter } from "@/helpers/data";

type OfferLineType = 'ACTION' | 'MATERIAL' | 'CUSTOM';

type LineModel = {
    local_id: number;
    action_type?: any;
    time_unit?: any;
    material?: any;
    inventory_item_id?: string | number | null;
    inventory_unit?: any;
    is_unit_based: boolean;
    description: string;
    quantity: number;
    unit_cost: number;
    discount_percentage: number;
    notes: string;
};

@Component
export default class AddOfferLineItemModal extends Vue {
    @Prop() payload: any

    isAddingOfferLineItem = false;
    lineItemsTouched = false;

    greekSearchFilter = greekSearchFilter;

    lineTypes = {
        ACTION: false,
        MATERIAL: false,
        CUSTOM: false,
    };

    actionLines: LineModel[] = [this.newLine('ACTION')];
    materialLines: LineModel[] = [this.newLine('MATERIAL')];
    customLines: LineModel[] = [this.newLine('CUSTOM')];

    actionRules = [
        (val: any) => (!!val && val.text != "") || this.$i18n.t("action_required"),
    ]
    unitOptionRules = [
        (val: any) => ((!!val || val == '') && val.text.length > 0) || this.$i18n.t("unitOptionRule"),
    ]
    quantityRules = [
        (val: any) => Number(val) >= 1 || this.$i18n.t("requiredNonZeroQuantityRule"),
    ]
    unitCostRules = [
        (val: any) => val !== '' && val !== null || this.$i18n.t("priceOfferRequiredRule"),
        (val: any) => Number(val) >= 0 || this.$i18n.t("priceOfferNonNegativedRule"),
    ]
    materialRules = [
        (val: any) => {
            if (!val && val !== 0) return this.$i18n.t("materialFieldRequiredRule");
            if (typeof val === 'string') return val.trim().length > 0 || this.$i18n.t("materialFieldRequiredRule");
            return val.text?.trim().length > 0 || this.$i18n.t("materialFieldRequiredRule");
        }
    ]
    descriptionRules = [
        (val: any) => !!val || this.$i18n.t("descriptionRequiredRule"),
        (val: any) => val.length <= 200 || this.$i18n.t("descriptionLengthRule"),
    ]
    discountRule = [
        (val: any) => val !== '' && val !== null && Number(val) >= 0 && Number(val) <= 100 || this.$i18n.t("discountPercentageRule"),
    ]
    lineItemNotesRules = [
        (val: any) => !val || val.length <= 500 || this.$i18n.t("offerLineItemNotesLengthRule"),
    ]

    get actionsCombo() { return actionTypesModule.actionTypesCombo; }
    get inventoryItemsCombo(): any[] { return commonModule.inventoryItemsCombo; }
    get itemsUnitsTypes(): any[] { return itemsModule.itemsUnitsCombo; }
    get timeChargedUnitCombo() { return commonModule.timeChargedUnitCombo; }
    get hasSelectedLineType() {
        return this.lineTypes.ACTION || this.lineTypes.MATERIAL || this.lineTypes.CUSTOM;
    }

    get nextSortOrder() {
        const lineItems = this.payload?.offer?.line_items || this.payload?.offerDetails?.line_items || [];
        if (!lineItems.length) return 0;
        return Math.max(...lineItems.map((li: any) => Number(li.sort_order || 0))) + 1;
    }

    async mounted() {
        commonModule.initSnackbar({});
        await Promise.all([
            actionTypesModule.getActionTypesCombo(),
            commonModule.getItemsCombo(),
            commonModule.getTimeChargedUnitCombo(),
            itemsModule.getItemsUnitsCombo(),
        ]);
    }

    newLine(lineType: OfferLineType): LineModel {
        return {
            local_id: Math.random() * -1,
            action_type: lineType === 'ACTION' ? null : undefined,
            time_unit: null,
            material: lineType === 'MATERIAL' ? null : undefined,
            inventory_item_id: null,
            inventory_unit: null,
            is_unit_based: false,
            description: '',
            quantity: 1,
            unit_cost: 0,
            discount_percentage: 0,
            notes: '',
        };
    }

    addLine(lineType: OfferLineType) {
        this.linesByType(lineType).push(this.newLine(lineType));
    }

    deleteLine(lineType: OfferLineType, index: number) {
        const lines = this.linesByType(lineType);
        if (lines.length > 1) lines.splice(index, 1);
    }

    linesByType(lineType: OfferLineType): LineModel[] {
        switch (lineType) {
            case 'ACTION': return this.actionLines;
            case 'MATERIAL': return this.materialLines;
            case 'CUSTOM': return this.customLines;
            default: return [];
        }
    }

    onActionTypeChange(line: LineModel) {
        if (!line.action_type) return;
        line.description = line.action_type.text || '';
        line.unit_cost = Number(line.action_type.cost || 0);
        line.time_unit = this.timeUnitOption(this.actionDefaultTimeUnit(line));

        line.quantity = 1; // Change it back to the default value as when the action does not have time_unit the user can not change the quantity
    }

    actionDefaultTimeUnit(line: LineModel): string | null {
        const actionType = line.action_type;
        const timeUnit = actionType?.time_unit || actionType?.unit?.unit || actionType?.unit;
        if (!actionType?.is_time_charged && !actionType?.time_unit) return null;
        return ['HOUR', 'DAY'].includes(String(timeUnit).toUpperCase()) ? String(timeUnit).toUpperCase() : null;
    }

    actionHasTimeUnit(line: LineModel) {
        return !!this.actionDefaultTimeUnit(line);
    }

    timeUnitOption(timeUnit: string | null) {
        if (!timeUnit) return null;
        return this.timeChargedUnitCombo.find((item: any) => item.unit === timeUnit) || null;
    }

    findItemUnit(unitType: Record<string, any> | string, line: LineModel) {
        if (!unitType) return;

        const selectedMaterial = typeof unitType === 'string' ? null : unitType;
        const foundUnitType: any = (this.itemsUnitsTypes as any[]).find((item: any) => item.enum_text == selectedMaterial?.unit);
        line.description = selectedMaterial?.text || (typeof unitType === 'string' ? unitType : '');
        line.inventory_unit = foundUnitType
            ? { text: foundUnitType.text, enum_text: foundUnitType.enum_text, decimals: foundUnitType.decimals }
            : null;
        line.is_unit_based = selectedMaterial?.is_unit_based ?? false;
        line.unit_cost = selectedMaterial?.cost ?? null;
        line.quantity = selectedMaterial?.is_unit_based ? 1 : line.quantity;
        line.inventory_item_id = selectedMaterial?.inventory_item_id ?? null;

        line.quantity = 1; // Change it back to the default value as when the action does not have time_unit the user can not change the quantity
    }

    optimalTextColor(color: string) {
        return this.isHexColor(color) ? getOptimalTextColor(color) : 'white';
    }

    isHexColor(color: string) {
        return /^#([0-9A-F]{3}){1,2}$/i.test(color);
    }

    buildLineItem(lineType: OfferLineType, line: LineModel, sortOrder: number) {
        const lineItem: Record<string, any> = {
            offerId: this.payload.offer.id,
            offer_id: this.payload.offer.id,
            installation_id: this.payload.offer.installation_id,
            line_type: lineType,
            description: line.description,
            quantity: Number(line.quantity),
            unit_cost: Number(line.unit_cost),
            discount_percentage: Number(Number(line.discount_percentage || 0).toFixed(2)),
            sort_order: sortOrder,
            notes: line.notes,
        };

        if (lineType === 'ACTION') {
            lineItem.action_type_id = line.action_type.id;
            const timeUnit = line.time_unit?.unit || line.time_unit;
            if (timeUnit) lineItem.time_unit = timeUnit;
        }

        if (lineType === 'MATERIAL') {
            lineItem.description = line.description || line.material?.text || (typeof line.material === 'string' ? line.material : '');

            if (line.inventory_item_id) lineItem.inventory_item_id = line.inventory_item_id;
            const inventoryUnit = line.inventory_unit?.enum_text || line.inventory_unit;
            if (inventoryUnit) lineItem.inventory_unit = inventoryUnit;
        }

        return lineItem;
    }

    buildLineItems() {
        const items: { type: OfferLineType; line: LineModel }[] = [];

        if (this.lineTypes.ACTION) this.actionLines.forEach(line => items.push({ type: 'ACTION', line }));
        if (this.lineTypes.MATERIAL) this.materialLines.forEach(line => items.push({ type: 'MATERIAL', line }));
        if (this.lineTypes.CUSTOM) this.customLines.forEach(line => items.push({ type: 'CUSTOM', line }));

        return items;
    }

    async hideModal() {
        commonModule.hideModal();
    }

    lineTotal(line: LineModel) {
        const quantity = Number(line.quantity || 0);
        const unitCost = Number(line.unit_cost || 0);
        const discountPercentage = Math.min(Math.max(Number(line.discount_percentage || 0), 0), 100);
        return formatAmount(quantity * unitCost * (1 - discountPercentage / 100));
    }

    async doAddOfferLineItem() {
        this.lineItemsTouched = true;

        if (!(this.$refs.form as Vue & { validate: () => boolean }).validate() || !this.hasSelectedLineType) {
            return;
        }

        this.isAddingOfferLineItem = true;

        try {
            let sortOrder = this.nextSortOrder;
            const lineItems = this.buildLineItems().map(({ type, line }) => this.buildLineItem(type, line, sortOrder++));

            await offersModule.createOfferLineItem(lineItems);

            this.hideModal();
        } finally {
            this.isAddingOfferLineItem = false;
        }
    }
}
